import React, { useContext } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Url } from '@wix/da-url';
import { VerificationRequiredActions } from '@wix/da-shared-react/pkg/publicSession';
import { useFlag } from '@wix/da-shared-react/pkg/redux/flags/hooks';
import { MobileContext } from '@wix/da-shared-react/pkg/Context';
import { PapiUser } from '@wix/da-types-papi';

import EmptyState from '../../../../EmptyState';
import PremiumFolderCreateButton from '../PremiumFolderCreateButton';

import s from './PremiumFoldersEmptyState.scss';

export interface Props {
  view: 'wide' | 'large';
  isVerificationNeeded: boolean;
  user: PapiUser;
  isOwner: boolean;
  pushVerificationPopup: (payload: VerificationRequiredActions) => void;
  onOpenChat: (user: PapiUser) => void;
}

export const PremiumFoldersEmptyState: React.FC<Props> = ({
  view,
  user,
  user: { username },
  isVerificationNeeded,
  isOwner,
  pushVerificationPopup,
  onOpenChat,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const isChatButtonsEnabled = useFlag('start_chat_button');

  const navigateToNotes = () => {
    if (isVerificationNeeded) {
      pushVerificationPopup('notes');
      return;
    }

    const notesPath = Url.userNoteLink(username);
    window.location.assign(notesPath);
  };

  const { title, subtitle } = getText({
    t,
    username,
    isOwner,
    isMobile,
  });

  const handleOpenChat = () => onOpenChat(user);

  return (
    <EmptyState
      className={classnames(s['root'], s[view])}
      title={title}
      subtitle={subtitle}
      subtitleClassName={s['subtitle']}
      withButton={!isOwner}
      onButtonClick={isChatButtonsEnabled ? handleOpenChat : navigateToNotes}
      buttonLabel={
        isChatButtonsEnabled
          ? t('pages.shop.emptyState.openChatWithArtist')
          : t('pages.shop.emptyState.messageArtist')
      }
      customButton={
        isOwner &&
        !isMobile && (
          <PremiumFolderCreateButton
            label={t('pages.shop.emptyState.addNewPremiumFolder')}
          />
        )
      }
    />
  );
};

function getText({
  t,
  username,
  isOwner,
  isMobile,
}: {
  t;
  username: string;
  isOwner: boolean;
  isMobile: boolean;
}) {
  if (!isOwner) {
    return {
      title: t('pages.shop.emptyState.premiumFolders.title.visitor', {
        username,
      }),
      subtitle: t('pages.shop.emptyState.premiumFolders.subtitle.visitor'),
    };
  }

  if (isMobile) {
    return {
      title: t('pages.shop.emptyState.premiumFolders.title.owner.mobile'),
      subtitle: t('pages.shop.emptyState.premiumFolders.subtitle.owner.mobile'),
    };
  }

  return {
    title: t('pages.shop.emptyState.premiumFolders.title.owner'),
    subtitle: t('pages.shop.emptyState.premiumFolders.subtitle.owner'),
  };
}

PremiumFoldersEmptyState.displayName = 'PremiumFoldersEmptyState';

export default PremiumFoldersEmptyState;
